/**
 * Filtro de carteira (só administradores, nas telas Negociação e Cadastros > Clientes):
 * um botão com a lista Todos · Administradores (carteira compartilhada) · cada vendedor.
 * e faixa de "visualizando como" (suporte). O filtro fica lembrado entre as duas telas.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ROLE_LABEL } from '@dotapps/core';
import { Avatar, Icon } from '@dotapps/ui';
import { getCarteira, setCarteira, subscribeCarteira } from '../lib/api';
import { useUsers } from '../lib/queries';
import { useAuth } from './auth';

/** Filtro em uso (para quem não é administrador, sempre a própria carteira, que é o que a API devolve). */
export function useCarteira(): string {
  const { isAdmin } = useAuth();
  const value = useSyncExternalStore(subscribeCarteira, getCarteira);
  return isAdmin ? value : 'all';
}

interface Option {
  value: string;
  label: string;
  sub: string;
  ini?: string;
  /** vaga de vendedor ainda sem cadastro */
  empty?: boolean;
}

export function CarteiraBar() {
  const { isAdmin } = useAuth();
  const users = useUsers();
  const people = (users.data ?? []).filter(u => !u.support);
  const admins = people.filter(u => u.role === 'admin' && u.active);
  const sellers = people.filter(u => u.role === 'vendedor' && u.active);
  const value = useCarteira();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  // filtro salvo de alguém que saiu da equipe: volta para todos os clientes
  const current = value === 'all' || value === 'me' || sellers.some(s => s.id === value) ? value : 'all';
  const stale = !!users.data && current !== value;
  useEffect(() => {
    if (stale) setCarteira('all');
  }, [stale]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!isAdmin) return null;

  const options: Option[] = [
    { value: 'all', label: 'Todos os clientes', sub: 'Escritório inteiro' },
    {
      value: 'me',
      label: 'Carteira dos administradores',
      sub: admins.map(a => a.name.split(' ')[0]).join(' e '),
      ini: admins[0]?.ini,
    },
    ...sellers.map((s, i) => ({ value: s.id, label: s.name, sub: `Vendedor ${i + 1}`, ini: s.ini })),
    // sempre as duas visões de vendedor; sem cadastro, a vaga aparece indicando onde cadastrar
    ...Array.from({ length: Math.max(0, 2 - sellers.length) }, (_, k) => ({
      value: `vaga-${k}`,
      label: `Vendedor ${sellers.length + k + 1}`,
      sub: 'Ainda não cadastrado · Equipe › Nova pessoa',
      empty: true,
    })),
  ];
  const sel = options.find(o => o.value === current) ?? options[0];

  return (
    <div className="cart" ref={box}>
      <button
        type="button"
        className={`cart-btn${open ? ' on' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        {sel.ini ? <Avatar ini={sel.ini} size="xs" /> : <Icon name="users" />}
        <span>{sel.label}</span>
        <Icon name="chevron" />
      </button>
      {open && (
        <ul className="cart-menu" role="listbox" aria-label="Carteira">
          {options.map(o => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={o.value === current}
                className={o.value === current ? 'on' : o.empty ? 'empty' : ''}
                disabled={o.empty}
                onClick={() => {
                  setCarteira(o.value);
                  setOpen(false);
                }}
              >
                {o.ini ? <Avatar ini={o.ini} size="xs" /> : <Icon name={o.empty ? 'users' : 'building'} />}
                <span>
                  <b>{o.label}</b>
                  <small>{o.sub}</small>
                </span>
                {o.value === current && <Icon name="check" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Suporte visualizando como outra pessoa: aviso fixo e volta ao próprio acesso. */
export function ViewAsBanner() {
  const { user, viewAs, stopViewAs } = useAuth();
  if (!user || !viewAs) return null;
  return (
    <div className="viewas" role="status">
      <Icon name="eye" />
      <span>
        Visualizando como <b>{user.name}</b> · {ROLE_LABEL[user.role]} <em>somente leitura</em>
      </span>
      <button type="button" className="btn sm" onClick={stopViewAs}>
        <Icon name="back" />
        Voltar ao meu acesso
      </button>
    </div>
  );
}
